import React, { useCallback, useEffect, useMemo, useState } from "react";
import CustomSideBar from "../../../shadcn/components/custom/CustomSideBar";
import { useRouter } from "next/router";
import HomeIcon from "../../../icons/HomeIcon";
import CalendarIcon from "../../../icons/CalendarIcon";
import EventIcon from "../../../icons/EventIcon";
import LiveTvIcon from "../../../icons/LiveTvIcon";
import DocIcon from "../../../icons/DocIcon";
import LoginSvg from "../../../icons/LoginSvg";
import {
  SidebarInset,
  SidebarProvider,
  SidebarTrigger,
} from "../../../shadcn/components/ui/sidebar";
import LogoSvg from "../../../icons/Logo";
import UserIcon from "../../../icons/UserIcon";

import { useSelector, useDispatch } from "react-redux";
import {
  clearAuth,
  clearBidderAuth,
  clearGuestUser,
  setBidderPortalAccessType,
} from "store";
import Loader from "app/layouts/Loader";
import { getAuctionEventsForBidder, getBidderByUserUuid } from "services";

interface BidderDashboardLayoutV2Props {
  children: React.ReactNode;
}

export default function BidderDashboardLayoutV2({
  children,
}: BidderDashboardLayoutV2Props) {
  const router = useRouter();
  const dispatch = useDispatch();
  const [currentActiveTab, setCurrentActiveTab] = useState<any>();
  const [isLoggingOut, setIsLoggingOut] = useState(false);
  const portalAccessType = useSelector(
    (state: any) => state?.bidderAuth?.portalAccessType,
  );
  const onlySwissEventUser = portalAccessType === "swiss";
  const userAttributes = useSelector((state: any) => {
    return state.bidderAuth?.user?.attributes;
  });
  // const userProfile = useSelector((state: any) => {
  //   return state.bidderAuth.user?.profile;
  // });
  const onboardingData = useSelector((state: any) => {
    return state.bidderAuth.onboarding;
  });
  const user = useSelector((state: any) => {
    return state?.bidderAuth?.user;
  });

  const validateIfUserIsSwissBidder = useCallback(async () => {
    if (user?.uuid === undefined) return;
    const eligibleBidder: any = await getBidderByUserUuid(user?.uuid);
    const eventUuidsForBidder = eligibleBidder.map(
      (item: any) => item?.event_uuid,
    );
    if (eventUuidsForBidder.length === 0) {
      dispatch(setBidderPortalAccessType("normal"));
      return;
    }
    const allEventsData: any = await getAuctionEventsForBidder(
      eventUuidsForBidder,
      [
        "pending",
        "accepted",
        "live",
        "end",
        "cancelled",
        "pending_bid_approval",
      ],
      false,
    );
    const allEvents = Array.isArray(allEventsData)
      ? allEventsData
      : allEventsData?.data ?? [];
    const swissEvents = allEvents.filter(
      (event: any) => event?.event_type === "Asset transfer",
    );
    const nonSwissEvents = allEvents.filter(
      (event: any) => event?.event_type !== "Asset transfer",
    );

    if (nonSwissEvents?.length === 0 && swissEvents?.length > 0) {
      dispatch(setBidderPortalAccessType("swiss"));
    } else {
      dispatch(setBidderPortalAccessType("normal"));
    }
  }, [dispatch, user?.uuid]);

  const tabs = useMemo(
    () => [
      {
        tabId: "home_page",
        text: "Homepage",
        href: "/",
        icon: <HomeIcon />,
      },
      ...(onlySwissEventUser
        ? []
        : [
            {
              tabId: "all_events",
              text: "All Events",
              href: "/all-events",
              icon: <CalendarIcon className="w-5 h-5" />,
            },
          ]),
      {
        tabId: "my_events",
        text: "My Events",
        href: "/auction-events",
        icon: <EventIcon />,
      },
      // {
      //   tabId: "live_events",
      //   text: "Live Events",
      //   href: "/live-events",
      //   icon: <LiveTvIcon />,
      // },
      {
        tabId: "documents",
        text: "Documents",
        href: "/documents",
        icon: <DocIcon />,
      },
    ],
    [onlySwissEventUser],
  );
  const tabClickHandler = (tab: any) => {
    setCurrentActiveTab(tab?.tabId);
    router.push(tab.href, undefined, { shallow: true });
    // Add additional logic here as needed
  };

  const logoutHandler = async () => {
    setIsLoggingOut(true);
    await fetch("/api/logout", {
      method: "GET",
      headers: { "Content-Type": "application/json" },
    });
    router.replace(`/login`).then(() => {
      setIsLoggingOut(false);
      // window.scrollTo(0, 0);
    });
    dispatch(clearBidderAuth());
    dispatch(clearAuth());
    dispatch(clearGuestUser());
    localStorage.removeItem("bidder_redirect_uri");
    // localStorage.removeItem("2faVerifiedDate");
    // localStorage.removeItem("tabIds");
    // sessionStorage.removeItem("tabId");
    // sessionStorage.removeItem("refreshing");
  };

  useEffect(() => {
    (async () => {
      await validateIfUserIsSwissBidder();
      const tabFound = tabs.filter((obj: any) => obj.href === router.pathname);
      if (tabFound && tabFound.length > 0) {
        setCurrentActiveTab(tabFound[0]?.tabId);
      }
    })();
  }, [router?.pathname, tabs, validateIfUserIsSwissBidder]);

  return (
    <div className="">
      {isLoggingOut === true ? (
        <main className="justify-center max-w-7xl mx-auto flex flex-col items-center min-h-screen">
          <Loader height="h-16" width="w-16" color="text-brand-orange" />
        </main>
      ) : (
        <SidebarProvider>
          <CustomSideBar
            tabs={tabs}
            currentActiveTabId={currentActiveTab}
            tabOnClickHandler={tabClickHandler}
            logo={<LogoSvg type="bidder" />}
            footer={
              <div>
                <div className="flex lg:hidden items-center justify-between text-sm font-sans gap-1 cursor-pointer bg-brand-bgLightGrey py-4">
                  <div className="w-[173px] flex items-center px-3 border-l-[1px] border-brand-borderGrey">
                    <div className="p-[4px] rounded-full bg-[#eee]">
                      <UserIcon width={19} height={19} />
                    </div>
                    <div className="ml-4 font-sans text-xs">
                      <div className="font-sans text-brand-textBlack tracking-wider">
                        {onboardingData?.name || "user"}
                      </div>
                      <div className="text-brand-borderGrey text-xs">
                        {userAttributes && userAttributes[0].value}
                      </div>
                    </div>
                  </div>

                  <div className="" onClick={logoutHandler}>
                    <LoginSvg
                      height="40"
                      width="40"
                      bg_color="bg-transparent"
                    />
                  </div>
                </div>
                <div
                  className="hidden lg:flex items-center text-sm font-sans gap-1 cursor-pointer"
                  onClick={logoutHandler}
                >
                  <LoginSvg height="40" width="40" bg_color="bg-transparent" />
                  <div className="text-brand-textGrey hover:text-brand-textBlack">
                    Logout
                  </div>
                </div>
              </div>
            }
          />
          <SidebarInset className="bg-[#F8F8F8] max-w-7xl">
            <header className="h-[102px] lg:h-[80px] lg:ml-[13px] flex flex-col lg:flex-row items-center py-6 shrink-0 gap-2 ease-linear group-has-[[data-collapsible=icon]]/sidebar-wrapper:h-12 bg-white border-b-[1px] border-l-[1px] border-[#EEE]">
              <div className="w-full flex items-center gap-2 px-4">
                <SidebarTrigger className="-ml-1 lg:hidden" />
                <div className="pl-4 lg:hidden">
                  <LogoSvg type="bidder" />
                </div>

                <div className="w-full hidden lg:flex justify-between">
                  <div className="font-sans text-brand-textBlack text-lg capitalize tracking-wider">
                    Hello {onboardingData?.name || "User"},
                  </div>
                  <div className="w-[269px] flex items-center px-5 border-l-[1px] border-brand-borderGrey">
                    <div className="p-[6px] rounded-full bg-[#eee]">
                      <UserIcon width={19} height={19} />
                    </div>
                    <div className="ml-4 font-sans text-xs">
                      <div className="font-sans text-brand-textBlack tracking-wider">
                        {onboardingData?.name || "User"}
                      </div>
                      <div className="text-brand-borderGrey text-xs">
                        {userAttributes && userAttributes[0].value}
                      </div>
                    </div>
                  </div>
                </div>
              </div>
              <div className="w-full px-4 lg:hidden">
                <div className="font-sans text-brand-textBlack text-xl capitalize tracking-wider">
                  Hello {onboardingData?.name || "User"},
                </div>
              </div>
            </header>

            <div className="flex flex-1 flex-col gap-4 py-6 lg:py-6 px-4 lg:px-8 bg-[#F8F8F8] max-w-7xl selection:overflow-scroll">
              {children}
            </div>
          </SidebarInset>
        </SidebarProvider>
      )}
    </div>
  );
}
